// THE `kb_learnings` PAYLOAD — the wiki page a review run wrote back, and the slug
// prefix its pages are namespaced under.
// Split out of `../task-run-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';

/**
 * Namespace a `*_learnings` event may write. The reviewer's write-back is the
 * REVIEW half of the repo wiki (`review/pr-<n>`, `review/pr-<n>-decisions`), so
 * the wire contract confines it there: a box is untrusted input, and an
 * unconfined slug would let a buggy (or compromised) runner replace a
 * human-curated page — `kb_page_upsert` is create-or-REPLACE by slug.
 */
export const KB_LEARNINGS_SLUG_PREFIX = 'review/';

/**
 * One wiki page a runner rendered for the mothership to upsert VERBATIM (#260).
 * Field-for-field the `kb_page_upsert` verb's input (`packages/mcp` `tools.ts`
 * `KbPageUpsertInputSchema`) — same slug alphabet, same body cap, same link cap —
 * so a page that clears this wire gate can never be one the KB write rejects.
 */
/**
 * The wiki slug alphabet — the hand-mirror of `@developerz/mcp`'s
 * `KB_SLUG_PATTERN` (`tools.ts`), which domain cannot import (mcp depends on
 * domain, not the reverse). Declared ONCE here because {@link KbWikiPageSchema}
 * spells it for BOTH a page's own slug and the sibling slugs it links to, and
 * two spellings is how they diverged: `links` carried a bare length bound, so
 * `['../../etc/passwd']` cleared this gate. The write is not through the verb —
 * the event-sink calls `upsertRepoMemoryPage` directly — so nothing downstream
 * re-checked it, and the dashboard's `kbPagePath` throws on the `..` segment
 * when the page renders.
 */
const KB_WIKI_SLUG = /^[a-z0-9][a-z0-9/_-]{0,199}$/i;

export const KbWikiPageSchema = z.object({
  slug: z
    .string()
    .trim()
    .regex(KB_WIKI_SLUG, 'slug: letters, digits, slash, underscore, hyphen (≤200)')
    .refine(
      (s) => s.startsWith(KB_LEARNINGS_SLUG_PREFIX),
      `slug: a learnings page must live under "${KB_LEARNINGS_SLUG_PREFIX}"`,
    ),
  /** Full markdown body — the consumer upserts it verbatim, never re-derives it. */
  text: z.string().min(1).max(100_000),
  /** `[[wikilink]]` slugs to sibling pages. NOT namespace-confined: a link creates no page. */
  links: z
    .array(z.string().trim().regex(KB_WIKI_SLUG, 'link: a sibling page slug'))
    .max(200)
    .default([]),
});

/**
 * The KB write-half a review runner streams (#260): a rendered wiki page (+ an
 * optional append-only log line) the mothership applies to `repo_memory`. Pure
 * wire shape — the mothership detects exactly this and no other status event
 * ({@link PlanAuthoredPayloadSchema} is the sibling precedent).
 *
 * There is no runner→mothership KB transport (a box's creds grant only
 * `fleet.evt.<run_id>`), so the review handler's `writeLearnings` seam emits this
 * as an audited status event and the event-sink's KB consumer performs the write.
 * `learnings` (the full finding set) rides along on the wire and stays in the
 * durable `task_run_events` row; it is deliberately NOT parsed here — the page IS
 * the write, so the consumer never re-derives one from raw findings. Only its
 * length, `count`, is read: the consumer decides WHETHER to write on it.
 */
export const KbLearningsPayloadSchema = z.object({
  /** `review_learnings` (a PR's findings page) | `thread_learnings` (its decisions page). */
  event: z.enum(['review_learnings', 'thread_learnings']),
  pr_number: z.number().int().positive(),
  wiki_page: KbWikiPageSchema,
  /** Grep-able `## [<iso>] <kind> | …` entry for the wiki's chronological half. */
  log_line: z.string().min(1).max(2_000).optional(),
  /**
   * How many findings the rendered page carries — the runner's `learnings.length`
   * (`emitReviewLearnings`), which is exactly what decides whether `reviewLearningsPage`
   * renders a `## Findings` section at all. A STRUCTURAL signal, so the consumer can
   * tell a zero-finding review without string-matching markdown. Optional because an
   * older box, or a decisions event, may not send it: absent means "unknown", and the
   * consumer treats unknown as "write" rather than guess a page empty.
   */
  count: z.number().int().nonnegative().optional(),
});
export type KbLearningsPayload = z.infer<typeof KbLearningsPayloadSchema>;
